import { apiRequest, buildApiHeaders } from './api';

/** فایل‌های کوچک با fetch (همان مسیر قبلی apiRequest) — XHR فقط برای فایل‌های بزرگ */
const LARGE_FILE_XHR_THRESHOLD = 5 * 1024 * 1024;

async function uploadViaFetch(url, formData, { signal } = {}) {
  return apiRequest(url, { method: 'POST', body: formData, signal });
}

/**
 * آپلود FormData — بدون محدودیت حجم سمت کلاینت + گزارش پیشرفت
 * تصاویر و فایل‌های کوچک: fetch (پایدارتر با proxy)
 * فایل‌های بزرگ: XHR با progress واقعی
 */
export function uploadFormDataWithProgress(url, formData, { onProgress, signal, fileSize = 0 } = {}) {
  const useXhr =
    typeof onProgress === 'function' && Number(fileSize) >= LARGE_FILE_XHR_THRESHOLD;

  if (!useXhr) {
    return (async () => {
      onProgress?.(0);
      const result = await uploadViaFetch(url, formData, { signal });
      onProgress?.(100);
      return result;
    })();
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;

    const headers = buildApiHeaders({ method: 'POST', body: formData });
    Object.entries(headers).forEach(([key, value]) => {
      if (key.toLowerCase() === 'content-type') return;
      xhr.setRequestHeader(key, value);
    });

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || typeof onProgress !== 'function') return;
      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };

    xhr.onload = () => {
      let payload = {};
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        payload = { raw: xhr.responseText };
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload);
        return;
      }

      const err = new Error(
        payload?.message ||
          payload?.error?.message ||
          `خطا در آپلود فایل (${xhr.status})`,
      );
      err.status = xhr.status;
      err.data = payload;
      reject(err);
    };

    xhr.onerror = () =>
      reject(
        new Error(
          'اتصال هنگام آپلود قطع شد. اگر فایل بزرگ است، محدودیت سرور (nginx/PHP) را بررسی کنید.',
        ),
      );
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

    xhr.send(formData);
  });
}

/** آپلود چند فایل — هر فایل در یک درخواست جدا (پایدارتر برای فایل‌های حجیم) */
export async function uploadFilesSequential({
  url,
  buildFormData,
  files,
  onFileProgress,
  onFileComplete,
}) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!list.length) return null;

  const responses = [];
  for (let index = 0; index < list.length; index += 1) {
    const file = list[index];
    const formData = buildFormData(file);
    const res = await uploadFormDataWithProgress(url, formData, {
      fileSize: file.size || 0,
      onProgress: (percent) => {
        onFileProgress?.({
          index,
          total: list.length,
          percent,
          fileName: file.name,
        });
      },
    });
    responses.push(res);
    onFileComplete?.({ index, total: list.length, file, response: res });
  }

  if (responses.length === 1) return responses[0];
  return { files: responses, responses };
}
